import type { Query, QueryClient } from "@tanstack/react-query";
import type { components } from "./contract";

type Asset = components["schemas"]["V2Asset"];
type CacheOptions = { collections?: boolean };

/** Asset mutations must never refetch generation quotes or model catalogs. */
export function isAssetQuery(query: Query, spaceID: string, options: CacheOptions = {}): boolean {
  const key = query.queryKey;
  if (key[1] !== spaceID) return false;
  if (key[0] === "foundation") {
    return (key[2] === "assets" && (key.length === 4 || key[3] === "graph" || key[3] === "image-edits")) ||
      (key[2] === "projects" && key[4] === "library") ||
      (options.collections === true && key[2] === "collections");
  }
  return key[0] === "generation" && (key[2] === "assets" || key[2] === "output-asset");
}

function isAsset(value: object): value is Asset {
  return "id" in value && "space_id" in value && "media_type" in value;
}

function patchValue(value: unknown, spaceID: string, updates: ReadonlyMap<string, Partial<Asset>>): unknown {
  if (!value || typeof value !== "object") return value;
  if (isAsset(value)) {
    const patch = value.space_id === spaceID ? updates.get(value.id) : undefined;
    return patch && Object.entries(patch).some(([key, next]) => value[key as keyof Asset] !== next)
      ? { ...value, ...patch } : value;
  }
  if (Array.isArray(value)) {
    const next = value.map(item => patchValue(item, spaceID, updates));
    return next.some((item, index) => item !== value[index]) ? next : value;
  }
  let result = value;
  // Flat indexes, paginated feeds, and project libraries share these envelopes.
  for (const key of ["pages", "items", "assets"] as const) {
    if (!(key in value)) continue;
    const current = (value as Record<string, unknown>)[key];
    const next = patchValue(current, spaceID, updates);
    if (next !== current) result = { ...result, [key]: next };
  }
  return result;
}

export function patchAssetCaches(client: QueryClient, spaceID: string, updates: ReadonlyMap<string, Partial<Asset>>): void {
  if (!updates.size) return;
  for (const query of client.getQueryCache().findAll({ predicate: query => isAssetQuery(query, spaceID) })) {
    const next = patchValue(query.state.data, spaceID, updates);
    if (next !== query.state.data) client.setQueryData(query.queryKey, next);
  }
}

export async function invalidateAssetQueries(client: QueryClient, spaceID: string, options: CacheOptions = {}): Promise<void> {
  await client.invalidateQueries({ predicate: query => isAssetQuery(query, spaceID, options) });
}

const assetIndexes = new WeakMap<object, Map<string, Asset>>();
function indexAssets(value: unknown, result: Map<string, Asset>): void {
  if (!value || typeof value !== "object") return;
  if (isAsset(value)) { result.set(value.id, value); return; }
  if (Array.isArray(value)) {
    value.forEach(item => indexAssets(item, result));
    return;
  }
  for (const key of ["pages", "items", "assets"] as const) {
    indexAssets((value as Record<string, unknown>)[key], result);
  }
}

/** Read the freshest copy even before its query observer has delivered new props. */
export function cachedAsset(client: QueryClient, asset: Asset): Asset {
  const queries = client.getQueryCache().findAll({ predicate: query => isAssetQuery(query, asset.space_id) })
    .sort((a, b) => b.state.dataUpdatedAt - a.state.dataUpdatedAt);
  for (const query of queries) {
    const data = query.state.data;
    if (!data || typeof data !== "object") continue;
    let index = assetIndexes.get(data);
    if (!index) { index = new Map(); indexAssets(data, index); assetIndexes.set(data, index); }
    const found = index.get(asset.id);
    if (found && found.space_id === asset.space_id) return found;
  }
  return asset;
}
